import { useCallback, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@il-colpo/engine';
import { Badge, Button } from '../../ui/index.ts';
import { RoleFigure } from '../figures/RoleFigure.tsx';
import { useGame } from '../GameContext.tsx';
import { unit } from './Banknotes.tsx';
import styles from './SuspectBoard.module.css';

export interface Suspect {
  id: string;
  name: string;
  role: Role;
  seat: number;
  /** Who this player has pinned, if anyone. */
  pin: string | null;
  mine: boolean;
}

interface Thread {
  key: string;
  d: string;
}

/** A thread from the pin of one card to the pin of another, sagging a little under its weight. */
export function threadPath(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const mx = (from.x + to.x) / 2;
  const sag = 14 + Math.hypot(to.x - from.x, to.y - from.y) * 0.12;
  const my = (from.y + to.y) / 2 + sag;
  return `M${from.x.toFixed(1)} ${from.y.toFixed(1)} Q${mx.toFixed(1)} ${my.toFixed(1)} ${to.x.toFixed(1)} ${to.y.toFixed(1)}`;
}

/**
 * The board of suspects (ART_BIBLE §8): everyone at the table on a cork board, a polaroid each,
 * pinned to it; whoever is pinned by someone is joined to them by a red thread. The pin buttons
 * are the ones of the plain list: this only gives them a face.
 */
export function SuspectBoard({
  suspects,
  mine,
  onPin,
}: {
  suspects: readonly Suspect[];
  /** Who the player has pinned. */
  mine: string | null;
  onPin: (target: string | null) => void;
}) {
  const { t } = useTranslation('game');
  const { g } = useGame();
  const board = useRef<HTMLUListElement>(null);
  const cards = useRef(new Map<string, HTMLLIElement>());
  const [threads, setThreads] = useState<Thread[]>([]);

  const key = suspects.map((s) => `${s.id}>${s.pin ?? ''}`).join('|');
  const measure = useCallback(() => {
    const root = board.current;
    if (!root) return;
    const frame = root.getBoundingClientRect();
    const head = (id: string): { x: number; y: number } | null => {
      const el = cards.current.get(id);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left - frame.left + r.width / 2, y: r.top - frame.top + 10 };
    };
    const next: Thread[] = [];
    for (const s of suspects) {
      if (!s.pin) continue;
      const a = head(s.id);
      const b = head(s.pin);
      if (a && b) next.push({ key: `${s.id}>${s.pin}`, d: threadPath(a, b) });
    }
    setThreads(next);
  }, [suspects]);

  useLayoutEffect(() => {
    measure();
    const root = board.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => {
      ro.disconnect();
    };
    // `key` stands for who pins whom; the measuring itself depends on `suspects`.
  }, [key, measure]);

  return (
    <ul className={styles['board']} ref={board}>
      {suspects.map((s, i) => {
        const pinned = suspects.filter((q) => q.pin === s.id);
        return (
          <li
            key={s.id}
            ref={(el) => {
              if (el) cards.current.set(s.id, el);
              else cards.current.delete(s.id);
            }}
            className={[styles['card'], s.mine ? styles['me'] : ''].join(' ')}
            style={{ '--tilt': `${((unit(i, 7) - 0.5) * 5).toFixed(1)}deg` } as CSSProperties}
          >
            <span className={styles['pinHead']} aria-hidden="true" />
            <RoleFigure role={s.role} seat={s.seat} size={72} className={styles['figure']} />
            <span className={styles['name']}>{s.name}</span>
            <Badge tone={s.mine ? 'cyan' : 'neutral'}>{g(`roles.${s.role}`)}</Badge>
            <span
              className={styles['pins']}
              aria-label={t('hideout.pinsOn', { count: pinned.length })}
            >
              {pinned.map((q) => (
                <span key={q.id} className={styles['mark']} title={q.name}>
                  {q.name.charAt(0)}
                </span>
              ))}
            </span>
            {s.mine ? null : (
              <Button
                size="md"
                variant="secondary"
                icon="pin"
                selected={mine === s.id}
                onClick={() => {
                  onPin(mine === s.id ? null : s.id);
                }}
              >
                {mine === s.id ? t('hideout.unpin') : t('hideout.pin')}
              </Button>
            )}
          </li>
        );
      })}
      <svg className={styles['threads']} aria-hidden="true" focusable="false">
        {threads.map((th) => (
          <path key={th.key} d={th.d} pathLength={1} />
        ))}
      </svg>
    </ul>
  );
}
